import React from "react";
import NoteIcon from "../common/NoteIcon";
import {
  formatDisplayDate,
  formatShortMonthDay,
  formatHours,
  getRowLabelDate,
  getTotalHourStep,
  getTotal
} from "../../utils/timecard";
import { useAutoExpandDayCard } from "../../hooks/useAutoExpandDayCard";
import { useTimeFormat } from "../../hooks/useTimeFormat";
import Field from "../common/Field";
import SectionHeading from "../common/SectionHeading";
import TimeCell from "../common/TimeCell";
import DayCard from "./DayCard";
import NotesDialog from "./NotesDialog";

export default function CallBackHoursSection({
  rows,
  onChange,
  getHospitalForDay,
  hospitalOptions = [],
  weekStartDate,
  readOnly = false
}) {
  const { setDayCardRef } = useAutoExpandDayCard();
  const { timeFormat } = useTimeFormat();
  const [notesIndex, setNotesIndex] = React.useState(null);

  return (
    <section className="sheet-section" aria-label="Call back hours">

      <div className="table-scroll compact-scroll">
        <table className="time-entry-table compact-table callback-table">
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">In #1</th>
              <th scope="col">Out #1</th>
              <th scope="col">In #2</th>
              <th scope="col">Out #2</th>
              <th scope="col" className="hours-column">Hours</th>
              <th scope="col">Missed break after 6 hours on a single callback</th>
              <th scope="col" className="notes-column">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
                <tr key={row.day}>
                  <th scope="row">
                    <div className="day-date-cell">
                      <span className="weekday">{row.day}</span>
                      {getRowLabelDate(row.date, weekStartDate, row.day)
                        ? <span className="date">{formatShortMonthDay(getRowLabelDate(row.date, weekStartDate, row.day))}</span>
                        : null}
                    </div>
                  </th>
                <td>
                  <TimeCell
                    value={row.timeInOne}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} callback in one`}
                    onChange={(value) => onChange(index, "timeInOne", value)}
                  />
                </td>
                <td>
                  <TimeCell
                    value={row.timeOutOne}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} callback out one`}
                    onChange={(value) => onChange(index, "timeOutOne", value)}
                  />
                </td>
                <td>
                  <TimeCell
                    value={row.timeInTwo}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} callback in two`}
                    onChange={(value) => onChange(index, "timeInTwo", value)}
                  />
                </td>
                <td>
                  <TimeCell
                    value={row.timeOutTwo}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} callback out two`}
                    onChange={(value) => onChange(index, "timeOutTwo", value)}
                  />
                </td>
                <td className="hours-column">
                  <input
                    type="number"
                    min="0"
                    step={getTotalHourStep(getHospitalForDay?.(row.day) ?? "", hospitalOptions)}
                    aria-label={`${row.day} callback hours`}
                    value={row.totalHours}
                    readOnly
                    disabled={readOnly}
                    onChange={(event) =>
                      onChange(index, "totalHours", event.target.value)
                    }
                  />
                </td>
                <td className="check-cell">
                  <input
                    type="checkbox"
                    aria-label={`${row.day} missed break after 6 hours on a single callback`}
                    checked={row.missedBreakAfterSix}
                    disabled={readOnly}
                    onChange={(event) => onChange(index, "missedBreakAfterSix", event.target.checked)}
                  />
                </td>
                <td className="notes-action-cell">
                  {(!readOnly || row.notes) && (
                    <button
                      type="button"
                      className={`notes-action-button${row.notes ? " has-note" : ""}`}
                      aria-label={`${row.notes ? "View or edit note" : "Add note"} for ${row.day}`}
                      onClick={() => setNotesIndex(index)}
                    >
                      <NoteIcon />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan="5" scope="row">
                Total call back hours for the week
              </th>
              <td className="hours-column">{formatHours(getTotal(rows))}</td>
              <td colSpan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <NotesDialog
        titleId="callback-notes-dialog-title"
        rows={rows}
        notesIndex={notesIndex}
        readOnly={readOnly}
        onChange={onChange}
        onClose={() => setNotesIndex(null)}
      />

      <div className="mobile-entry-list compact-entry-list" aria-label="Call back hours by day">
        {rows.map((row, index) => {
          const labelDate = getRowLabelDate(row.date, weekStartDate, row.day);
          const dateLabel = labelDate ? `${row.day} — ${formatDisplayDate(labelDate)}` : row.day;
          const hospital = getHospitalForDay?.(row.day) ?? "";
          return (
            <DayCard
              key={row.day}
              day={row.day}
              dateLabel={dateLabel}
              hospital={hospital}
              totalHours={row.totalHours}
              setDayCardRef={setDayCardRef}
            >
              <Field id={`callback-${row.day}-in-one`} label="Time in #1">
                <TimeCell
                  id={`callback-${row.day}-in-one`}
                  value={row.timeInOne}
                  format={timeFormat}
                  readOnly={readOnly}
                  ariaLabel={`${row.day} callback in one`}
                  onChange={(value) => onChange(index, "timeInOne", value)}
                />
              </Field>
              <Field id={`callback-${row.day}-out-one`} label="Time out #1">
                <TimeCell
                  id={`callback-${row.day}-out-one`}
                  value={row.timeOutOne}
                  format={timeFormat}
                  readOnly={readOnly}
                  ariaLabel={`${row.day} callback out one`}
                  onChange={(value) => onChange(index, "timeOutOne", value)}
                />
              </Field>
              <Field id={`callback-${row.day}-in-two`} label="Time in #2">
                <TimeCell
                  id={`callback-${row.day}-in-two`}
                  value={row.timeInTwo}
                  format={timeFormat}
                  readOnly={readOnly}
                  ariaLabel={`${row.day} callback in two`}
                  onChange={(value) => onChange(index, "timeInTwo", value)}
                />
              </Field>
              <Field id={`callback-${row.day}-out-two`} label="Time out #2">
                <TimeCell
                  id={`callback-${row.day}-out-two`}
                  value={row.timeOutTwo}
                  format={timeFormat}
                  readOnly={readOnly}
                  ariaLabel={`${row.day} callback out two`}
                  onChange={(value) => onChange(index, "timeOutTwo", value)}
                />
              </Field>
              <Field id={`callback-${row.day}-hours`} label="Total call back" full>
                <input
                  id={`callback-${row.day}-hours`}
                  type="number"
                  min="0"
                  step={getTotalHourStep(getHospitalForDay?.(row.day) ?? "", hospitalOptions)}
                  value={row.totalHours}
                  disabled={readOnly}
                  onChange={(event) =>
                    onChange(index, "totalHours", event.target.value)
                  }
                />
              </Field>
              <label className="toggle-field" htmlFor={`callback-${row.day}-missed-break`}>
                <span>Missed break after 6 hours on a single callback</span>
                <input
                  id={`callback-${row.day}-missed-break`}
                  type="checkbox"
                  checked={row.missedBreakAfterSix}
                  disabled={readOnly}
                  onChange={(event) => onChange(index, "missedBreakAfterSix", event.target.checked)}
                />
              </label>
              <Field id={`callback-${row.day}-notes`} label="Notes" full>
                <textarea
                  id={`callback-${row.day}-notes`}
                  rows="2"
                  value={row.notes}
                  disabled={readOnly}
                  onChange={(event) => onChange(index, "notes", event.target.value)}
                />
              </Field>
            </DayCard>
          );
        })}
      </div>
    </section>
  );
}
